<template>
  <v-container fluid>
    <v-layout row wrap>
      <!-- Header -->
      <v-flex class="d-flex justify-space-between mb-3 align-center" xs12>
        <h3 class="font-weight-bold">List Dynamic Routes</h3>
        <v-btn color="primary" dark small @click="createDynamicRoute">
          <v-icon left>mdi-plus</v-icon>
          Create Dynamic Route
        </v-btn>
      </v-flex>

      <!-- Search -->
      <v-flex class="pa-0" xs12>
        <v-card class="pa-6">
          <v-layout class="py-1 mx-2" row wrap>
            <v-flex class="pb-0" xs4>
              <v-text-field
                  :elevation="0"
                  class="hidden-sm-and-down"
                  flat
                  hide-details
                  placeholder="Search by typing here..."
                  prepend-inner-icon="mdi-magnify"
                  solo
                  v-model="searchQuery"
                  @input="searchRoutes"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <v-divider></v-divider>

          <!-- Data Table -->
          <v-card-text class="pa-0">
            <v-data-table
                :footer-props="footerProps"
                :headers="headers"
                :items="tableItems"
                :loading="loading"
                :options.sync="pagination"
                :server-items-length="totalNumber"
                item-key="id"
            >
              <!-- Status Toggle Button -->
              <template v-slot:item.status="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        @click="toggleStatus(item)"
                        :color="item.new_url === 1 ? 'success' : 'grey darken-1'"
                        dark
                        fab
                        small
                        class="ma-1"
                        :loading="item.isToggling"
                        elevation="2"
                    >
                      <v-icon medium>
                        {{ item.new_url === 1 ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ item.new_url === 1 ? 'Deactivate Route' : 'Activate Route' }}</span>
                </v-tooltip>
              </template>

              <!-- Action Buttons -->
              <template v-slot:item.action="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="edit(item)"
                        color="primary"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn
                        @click="confirmDelete(item)"
                        class="ml-2"
                        color="red lighten-1"
                        dark
                        dense
                        fab
                        v-on="on"
                        x-small
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>

          <!-- Snackbar -->
          <v-snackbar
              v-model="snackbar.show"
              :color="snackbar.color"
              bottom
              timeout="3000"
              style="margin: 0 auto;"
          >
            {{ snackbar.text }}
          </v-snackbar>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      searchQuery: "",
      loading: false,
      totalNumber: 0,
      pagination: {
        sortBy: ["id"],
        sortDesc: [false],
        page: 1,
        itemsPerPage: 5,
      },
      footerProps: {
        itemsPerPageOptions: [5, 10, 15, 25],
      },
      headers: [
        { text: "ID", value: "id", sortable: true },
        { text: "Route Type", value: "route_type", sortable: true },
        { text: "Route Name", value: "route_name", sortable: true },
        { text: "Route Url", value: "route_url", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Actions", value: "action", sortable: false },
      ],
      tableItems: [],
      snackbar: {
        show: false,
        text: "",
        color: "success",
      },
    };
  },
  watch: {
    pagination: {
      handler() {
        this.fetchRoutes();
      },
      deep: true,
    },
    searchQuery: {
      handler() {
        this.searchRoutes();
      },
    },
  },
  created() {
    this.fetchRoutes();
  },
  methods: {
    async fetchRoutes() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          limit: this.pagination.itemsPerPage,
          sortBy: this.pagination.sortBy[0],
          sortDesc: this.pagination.sortDesc[0],
          search: this.searchQuery,
        };
        const response = await axios.get(
            "https://backend.immigration.gov.np/api/v1/dynamic_routes",
            { params }
        );
        this.tableItems = response.data.data || [];
        this.totalNumber = response.data.total || this.tableItems.length;
      } catch (error) {
        console.error("Error fetching routes:", error);
        this.tableItems = [];
        this.totalNumber = 0;
      } finally {
        this.loading = false;
      }
    },

    searchRoutes() {
      this.pagination.page = 1;
    },

    createDynamicRoute() {
      this.$router.push({ name: "CreateDynamicRoute" });
    },

    edit(item) {
      this.$router.push({ name: "EditDynamicRoute", params: { id: item.id } });
    },

    async confirmDelete(item) {
      if (confirm(`Are you sure you want to delete ${item.route_name}?`)) {
        try {
          await axios.delete(
              `https://backend.immigration.gov.np/api/v1/dynamic_routes/${item.id}`
          );
          this.snackbar = {
            show: true,
            text: "Route deleted successfully!",
            color: "success",
          };
          this.fetchRoutes();
        } catch (error) {
          console.error(error);
          this.snackbar = {
            show: true,
            text: "Failed to delete route.",
            color: "error",
          };
        }
      }
    },

    async toggleStatus(item) {
      this.$set(item, "isToggling", true);
      const original = item.new_url;

      try {
        const response = await axios.post(
            `https://backend.immigration.gov.np/api/v1/dynamic_routes/${item.id}/toggle-status`
        );

        this.$set(item, "new_url", response.data.data.new_url);

        this.snackbar = {
          show: true,
          text: "Status toggled successfully!",
          color: "success",
        };

        this.fetchRoutes();
      } catch (error) {
        console.error("Failed to toggle status:", error);
        this.$set(item, "new_url", original);

        this.snackbar = {
          show: true,
          text: "Failed to toggle status.",
          color: "error",
        };
      } finally {
        this.$set(item, "isToggling", false);
      }
    },
  },
};
</script>